import {useEffect, useMemo, useRef, useState} from "react";
import {constructFilter, destructFilter, destructPage, destructSort} from "../../utils/APIUtils";
import useUpdateOnlyEffect from "../effect/useUpdateOnlyEffect";

export const PAGE_SIZE = 10;


export const SORT = {
  asc: "asc",
  desc: "desc",
  none: "none"
};

export const SORT_DIRECTIONS = {
  asc: SORT.desc,
  desc: SORT.none,
  none: SORT.asc
};

const NO_SELECTION = -1;

export const useTable = (state, selectionHandler, searchHandler, pageSize = PAGE_SIZE) => {
  const [selection, setSelection] = useState(undefined);
  const [params, setParams] = useState({page: {page: 0, size: pageSize, total: undefined}, filter: [], sort: {}, customParams: undefined});
  const [checked, setChecked] = useState({});
  const [blacklistFlag, setBlacklistFlag] = useState(false);
  const [[forceIncrement, forceFlag], setForceIncrement] = useState([0, false]);
  // const reduxState = useSelector(state => stateHandler(state));
  const list = state.data;
  const total = state.total;

  const autoSelectIndex = useRef(NO_SELECTION); //we use this to select first element when page is loaded, when we go to next page via select next

  const [allChecked, noneChecked, checkedCount] = useMemo(() => {
    const length = Object.keys(checked).length;
    const all = blacklistFlag && length === 0 || !blacklistFlag && length === total;
    const none = blacklistFlag && length === total || !blacklistFlag && length === 0;
    const checkedCount = blacklistFlag ? total - length : length;
    return [all, none, checkedCount];
  }, [checked, blacklistFlag, total]);

  function refreshList() {
    setForceIncrement([forceIncrement + 1, true]);
  }

  function onFilter(filter) {
    //if the filter is the same, don't reset page. Otherwise, we need to move to first page
    const page = constructFilter(filter) === state.filter ? params.page : {...params.page, page: 0};
    setParams({...params, filter: filter, page: page});
  }

  function onSort(sort) {
    setParams({...params, sort: sort});
  }

  function onPage(page, skipSelection = false) {
    if (selection && !skipSelection) {
      autoSelectIndex.current = list.indexOf(selection);
    }
    setParams({...params, page: {page: page, size: pageSize, total: total}});
  }

  function toLastPage() {
    const lastPage = Math.floor(total / pageSize) - (total % pageSize === 0 ? 1 : 0);
    onPage(lastPage, true);
  }

  function onSelection(selection) {
    setSelection(selection);
    selectionHandler && selectionHandler(selection);
  }

  function onCheck(key) {
    const state = {...checked};
    if (state[key] === !blacklistFlag) {
      delete state[key];
    } else {
      state[key] = !blacklistFlag;
    }
    setChecked(state);
  }

  function isChecked(key) {
    return blacklistFlag && checked[key] !== false || !blacklistFlag && checked[key];
  }

  function onCheckAll() {
    setBlacklistFlag(true);
    setChecked({});
  }

  function onCheckNone() {
    setBlacklistFlag(false);
    setChecked({});
  }

  function selectNextRow() {
    let index = list.indexOf(selection);
    if (index > -1) {
      if (++index >= list.length) {
        if ((params.page.page + 1) * params.page.size < params.page.total) {
          onPage(params.page.page + 1);
          autoSelectIndex.current = 0;
        }
      } else {
        onSelection(list[index]);
      }
    }
  }

  function isLastRowInPage() {
    const index = list.indexOf(selection);
    return index + 1 >= list.length;
  }

  function hasNextRow() {
    const index = list.indexOf(selection);
    if (index < 0) {
      return params.page.total > 0;
    }
    return params.page.total > params.page.page * params.page.size + index + 1;
  }

  useEffect(() => {
    setParams({...params, page: {...params.page, total: total}});
  }, [total]);

  useUpdateOnlyEffect(() => {
    if (list.length > 0 && autoSelectIndex.current >= 0) {
      onSelection(list[autoSelectIndex.current > list.length - 1 ? list.length - 1 : autoSelectIndex.current]);
      autoSelectIndex.current = NO_SELECTION;
    }
  }, [list]);

  useEffect(() => {
    const stateFilter = destructFilter(state?.filter);
    const stateSort = destructSort(state?.sort);
    const statePage = destructPage(state?.page);
    const total = state.total;

    setParams({
      filter: stateFilter,
      sort: stateSort ? stateSort : params.sort,
      page: statePage !== undefined ? {...params.page, page: statePage.page, size: statePage.size, total: total} : params.page
    });
  }, []);

  useUpdateOnlyEffect(() => {
    setForceIncrement([forceIncrement, false]);
    searchHandler && searchHandler(params.page, params.filter, params.sort, params.customParams, forceFlag);
  }, [params.filter, params.sort, params.page.size, params.page.page, forceIncrement]);

  return {
    list,
    refreshList,
    selection,
    onSelection,
    selectNextRow,
    hasNextRow,
    isLastRowInPage,
    page: params.page,
    onPage,
    toLastPage,
    filter: params.filter,
    onFilter,
    sort: params.sort,
    onSort,
    customParams: params.customParams,
    isChecked,
    checked,
    checkedCount,
    onCheck,
    allChecked,
    onCheckAll,
    noneChecked,
    onCheckNone,
    blacklistFlag,
    total,
  };
}
